/**
 * AI Insight floty (etap 5) — panel WWW (`fleet.web.ai.*`). Doklejone do `fleet.web` w `i18n/extra.ts`.
 * `fleetAiEn` jest typowany względem `fleetAiPl` — brak klucza = błąd kompilacji.
 * Teksty odchyleń (tytuł, opis, sugerowana czynność) przychodzą z backendu po polsku; UI tłumaczy rodzaje, kody czynności,
 * etykiety metryk i statusy po kodach.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetAiPl = {
  nav: 'AI Insight floty',
  title: 'AI Insight floty',
  description: 'Odchylenia wykryte w danych floty regułami i statystyką — z liczbami do sprawdzenia, odnośnikami i sugerowaną czynnością.',
  chipDeviations: 'Odchylenia',
  chipInsufficient: 'Dane niewystarczające',
  chipSuggestions: 'Sugestie',
  principle: 'To sygnały do sprawdzenia, nie ustalenia: system nie ocenia kierowców ani nie stwierdza przyczyn. Wykrywanie działa na danych i regułach, bez AI.',
  daysUnit: 'dni',
  filters: {
    kind: 'Rodzaj odchylenia',
    confidence: 'Pewność',
    dataStatus: 'Status danych',
    allKinds: 'Wszystkie rodzaje',
    allConfidence: 'Każda pewność',
    allStatuses: 'Każdy status danych',
  },
  kind: {
    LEASE_FORECAST_OVERAGE: 'Prognoza przekroczenia limitu leasingu',
    FUEL_CONSUMPTION_RISE: 'Wzrost zużycia paliwa',
    COST_PER_KM_HIGH: 'Wysoki koszt na km',
    ODOMETER_INCONSISTENT: 'Niespójne odczyty licznika',
    LOGBOOK_MISSING: 'Brak kart drogowych',
    INSPECTION_APPROACHING: 'Zbliżający się przegląd',
    PLAN_ACTUAL_DISCREPANCY: 'Rozbieżność plan / wykonanie',
  },
  confidence: { HIGH: 'Pewność wysoka', MEDIUM: 'Pewność średnia', LOW: 'Pewność niska' },
  confidenceHelp: 'Pewność mówi, jak solidne są dane, na których oparto wykrycie — nie jak poważne jest odchylenie ani czy ktoś zawinił.',
  dataStatus: { SUFFICIENT: 'Dane wystarczające', INDICATIVE: 'Wynik orientacyjny' },
  action: {
    CHECK_LEASE_PLAN: 'Sprawdź plan wykorzystania pojazdu',
    REVIEW_FUEL_ENTRIES: 'Przejrzyj tankowania i trasy',
    REVIEW_COSTS: 'Przejrzyj koszty pojazdu',
    VERIFY_ODOMETER: 'Zweryfikuj odczyty licznika',
    COMPLETE_LOGBOOKS: 'Uzupełnij karty drogowe',
    SCHEDULE_INSPECTION: 'Umów przegląd',
    REVIEW_ROUTE_PLAN: 'Sprawdź trasy plan / wykonanie',
  },
  ref: { vehicle: 'Pojazd', lease: 'Umowa', logbook: 'Karta drogowa', fuel: 'Tankowanie', expense: 'Koszt', incident: 'Zgłoszenie', route: 'Trasa', syncRun: 'Synchronizacja' },
  card: {
    evidence: 'Dane uzasadniające',
    action: 'Sugerowana czynność',
    links: 'Sprawdź w systemie',
    period: 'Okres: {from} – {to}',
    disclaimer: 'Sygnał z danych do sprawdzenia — nie ocena kierowcy ani ustalenie przyczyny.',
    relatedAlerts: 'Uzupełnia alert floty: {kinds}',
    confidenceReason: 'Dlaczego taka pewność: {reason}',
  },
  metric: {
    forecastEndKm: 'Prognozowany przebieg na koniec umowy',
    limitKm: 'Limit umowy',
    forecastOverageKm: 'Prognozowane przekroczenie',
    drivenKm: 'Przejechano od startu umowy',
    usedPercent: 'Wykorzystanie limitu',
    avgMonthlyKm: 'Średnio miesięcznie',
    allowedMonthlyKm: 'Mieszczące się w limicie tempo (miesięcznie)',
    daysRemaining: 'Dni do końca umowy',
    forecastOverageCost: 'Szacowany koszt przekroczenia',
    leaseEndDate: 'Koniec umowy',
    recentLPer100: 'Zużycie w ostatnim okresie',
    baselineLPer100: 'Zużycie w okresie porównawczym',
    risePct: 'Wzrost zużycia',
    riseLPer100: 'Wzrost zużycia (l/100 km)',
    recentKm: 'Przebieg w ostatnim okresie',
    baselineKm: 'Przebieg w okresie porównawczym',
    recentSegments: 'Odcinki w ostatnim okresie',
    baselineSegments: 'Odcinki w okresie porównawczym',
    recentLiters: 'Litry w ostatnim okresie',
    costPerKm: 'Koszt eksploatacji na km',
    peerMedianPerKm: 'Mediana grupy porównawczej',
    ratio: 'Krotność mediany',
    modifiedZ: 'Odchylenie od grupy (z-score MAD)',
    km: 'Przebieg w oknie',
    operatingCost: 'Koszt eksploatacji',
    peers: 'Pojazdy porównawcze',
    peerGroup: 'Grupa porównawcza',
    readingsBelowPrevious: 'Odczyty niższe niż poprzednie',
    jumpsAboveLimit: 'Podejrzanie duże przyrosty',
    maxKmPerDay: 'Największy przyrost na dobę',
    fuelEntriesBelowPrevious: 'Tankowania z niższym licznikiem',
    lastEventPreviousKm: 'Poprzedni odczyt',
    lastEventKm: 'Odczyt do sprawdzenia',
    lastEventDate: 'Data odczytu',
    undocumentedKm: 'Przebieg bez karty drogowej',
    segments: 'Odcinki bez karty',
    largestSegmentKm: 'Największy odcinek',
    firstSegmentFrom: 'Pierwszy odcinek od',
    lastSegmentTo: 'Ostatni odcinek do',
    windowDays: 'Okno analizy',
    inspectionDueAt: 'Termin przeglądu',
    daysLeft: 'Dni do terminu',
    plannedInspectionAt: 'Zaplanowany przegląd',
    avgKmPerDay: 'Średni przebieg dobowy',
    expectedKmUntilDue: 'Szacowany przebieg do terminu',
    routesCompared: 'Porównane trasy',
    routesOutsideTolerance: 'Trasy poza tolerancją',
    routesAbovePlan: 'Powyżej planu',
    routesBelowPlan: 'Poniżej planu',
    plannedKm: 'Km planowane',
    actualKm: 'Km z kart drogowych',
    deviationPct: 'Różnica względem planu',
    tolerancePct: 'Tolerancja',
    fromForecastOverageKm: 'Przekroczenie pojazdu źródłowego',
    fromForecastEndKm: 'Prognoza pojazdu źródłowego',
    fromLimitKm: 'Limit pojazdu źródłowego',
    toForecastEndKm: 'Prognoza pojazdu docelowego',
    toLimitKm: 'Limit pojazdu docelowego',
    toHeadroomKm: 'Zapas pojazdu docelowego',
    movedKm: 'Proponowane przesunięcie',
    daysElapsed: 'Dni umowy',
    vehiclesWithData: 'Pojazdy z danymi',
    recentIncompleteSegments: 'Niekompletne odcinki (ostatnio)',
    baselineIncompleteSegments: 'Niekompletne odcinki (porównanie)',
    routesWithoutLogbook: 'Trasy bez karty drogowej',
  },
  insufficient: {
    title: 'Dane niewystarczające',
    description: 'Tych sprawdzeń nie dało się wykonać. Brak wyniku nie oznacza, że wszystko jest w porządku — uzupełnij dane, a odchylenia pojawią się po przeliczeniu.',
    count: '{n} sprawdzeń bez wystarczających danych',
    fleetWide: 'Cała flota',
    reason: {
      FUEL_DATA_INCOMPLETE: 'Niekompletne odcinki tankowań (brak pełnego baku, luka, korekta licznika)',
      FUEL_NO_RECENT_DATA: 'Brak odcinków „pełny do pełnego” w ostatnim okresie',
      FUEL_NO_BASELINE: 'Brak historii zużycia do porównania',
      COST_KM_TOO_LOW: 'Za mały lub nieznany przebieg w oknie',
      COST_TOO_FEW_PEERS: 'Za mało porównywalnych pojazdów',
      PLAN_NO_ACTUAL: 'Trasy bez powiązanej karty drogowej',
      LEASE_NO_FORECAST: 'Brak danych do prognozy limitu',
    },
  },
  recommendations: {
    title: 'Sugestie optymalizacji',
    description: 'Scenariusze „co by było, gdyby” — do rozważenia, nie decyzje. Zawsze z założeniami.',
    move: 'Przesuń ok. {km} z {from} na {to}',
    savings: 'Potencjalna oszczędność: {amount}',
    assumptions: 'Założenia',
  },
  summary: {
    title: 'Podsumowanie AI',
    kindDaily: 'Dzienne',
    kindWeekly: 'Tygodniowe',
    generatedBy: 'Wygenerowane przez AI na podstawie podanych faktów z wykrywania odchyleń. Model nie dodaje własnych liczb; sprawdź dane w kartach poniżej.',
    mockNote: 'Tryb demonstracyjny: podsumowanie złożono szablonem z faktów, bez modelu językowego.',
    templateNote: 'Tekst modelu odrzucono (liczby spoza faktów lub niedozwolone sformułowania) — pokazujemy szablon złożony z faktów.',
    generatedAt: 'Wygenerowano {when}',
    stale: 'Podsumowanie jest nieświeże — odśwież je.',
    empty: 'Podsumowanie powstanie przy najbliższym przebiegu nocnym (06:00) albo po kliknięciu „Odśwież”.',
    refresh: 'Odśwież podsumowanie',
    refreshing: 'Generuję…',
    refreshed: 'Podsumowanie odświeżone.',
    regenLimit: 'Limit odświeżeń podsumowań AI na dziś został wykorzystany.',
    regenError: 'Nie udało się odświeżyć podsumowania. Spróbuj ponownie później.',
    noAccess: 'Podsumowania AI są dostępne dla kont z pełnym zakresem danych floty (umowy leasingu i karty drogowe). Lista odchyleń poniżej działa bez ograniczeń tego uprawnienia.',
    loadFailed: 'Nie udało się wczytać podsumowania.',
    model: 'Model: {model}',
  },
  recompute: { button: 'Przelicz teraz', running: 'Przeliczam…', done: 'Odchylenia przeliczone.', failed: 'Nie udało się przeliczyć odchyleń.' },
  states: {
    loadFailed: 'Nie udało się wczytać odchyleń.',
    empty: 'Nie wykryto odchyleń do sprawdzenia. Jeśli widzisz sekcję „Dane niewystarczające”, część sprawdzeń nie mogła zostać wykonana.',
    emptyFiltered: 'Brak odchyleń dla wybranych filtrów.',
    excluded: 'Pominięto sprawdzenia wymagające dodatkowych uprawnień: {kinds}.',
    generatedAt: 'Stan na {date} · {when}',
    cached: 'z pamięci podręcznej',
    fresh: 'świeżo przeliczone',
  },
} as const;

export const fleetAiEn: Shape<typeof fleetAiPl> = {
  nav: 'Fleet AI Insight',
  title: 'Fleet AI Insight',
  description: 'Deviations found in fleet data by rules and statistics — with verifiable numbers, links and a suggested action.',
  chipDeviations: 'Deviations',
  chipInsufficient: 'Insufficient data',
  chipSuggestions: 'Suggestions',
  principle: 'These are signals to check, not findings: the system does not judge drivers or state causes. Detection runs on data and rules, without AI.',
  daysUnit: 'days',
  filters: {
    kind: 'Deviation type',
    confidence: 'Confidence',
    dataStatus: 'Data status',
    allKinds: 'All types',
    allConfidence: 'Any confidence',
    allStatuses: 'Any data status',
  },
  kind: {
    LEASE_FORECAST_OVERAGE: 'Forecast lease limit overage',
    FUEL_CONSUMPTION_RISE: 'Fuel consumption rise',
    COST_PER_KM_HIGH: 'High cost per km',
    ODOMETER_INCONSISTENT: 'Inconsistent odometer readings',
    LOGBOOK_MISSING: 'Missing logbooks',
    INSPECTION_APPROACHING: 'Inspection approaching',
    PLAN_ACTUAL_DISCREPANCY: 'Plan vs actual discrepancy',
  },
  confidence: { HIGH: 'High confidence', MEDIUM: 'Medium confidence', LOW: 'Low confidence' },
  confidenceHelp: 'Confidence tells how solid the data behind the detection is — not how serious the deviation is or whether anyone is at fault.',
  dataStatus: { SUFFICIENT: 'Sufficient data', INDICATIVE: 'Indicative result' },
  action: {
    CHECK_LEASE_PLAN: 'Check the vehicle usage plan',
    REVIEW_FUEL_ENTRIES: 'Review fuel entries and routes',
    REVIEW_COSTS: 'Review the vehicle costs',
    VERIFY_ODOMETER: 'Verify odometer readings',
    COMPLETE_LOGBOOKS: 'Complete the logbooks',
    SCHEDULE_INSPECTION: 'Schedule the inspection',
    REVIEW_ROUTE_PLAN: 'Review plan vs actual routes',
  },
  ref: { vehicle: 'Vehicle', lease: 'Lease', logbook: 'Logbook', fuel: 'Fuel entry', expense: 'Expense', incident: 'Incident', route: 'Route', syncRun: 'Sync run' },
  card: {
    evidence: 'Supporting data',
    action: 'Suggested action',
    links: 'Check in the system',
    period: 'Period: {from} – {to}',
    disclaimer: 'A data signal to check — not a judgement of the driver or a finding of cause.',
    relatedAlerts: 'Complements fleet alert: {kinds}',
    confidenceReason: 'Why this confidence: {reason}',
  },
  metric: {
    forecastEndKm: 'Forecast mileage at lease end',
    limitKm: 'Lease limit',
    forecastOverageKm: 'Forecast overage',
    drivenKm: 'Driven since lease start',
    usedPercent: 'Limit used',
    avgMonthlyKm: 'Average per month',
    allowedMonthlyKm: 'Monthly pace that fits the limit',
    daysRemaining: 'Days to lease end',
    forecastOverageCost: 'Estimated overage cost',
    leaseEndDate: 'Lease end',
    recentLPer100: 'Consumption, recent period',
    baselineLPer100: 'Consumption, comparison period',
    risePct: 'Consumption rise',
    riseLPer100: 'Consumption rise (l/100 km)',
    recentKm: 'Mileage, recent period',
    baselineKm: 'Mileage, comparison period',
    recentSegments: 'Segments, recent period',
    baselineSegments: 'Segments, comparison period',
    recentLiters: 'Litres, recent period',
    costPerKm: 'Operating cost per km',
    peerMedianPerKm: 'Peer group median',
    ratio: 'Multiple of median',
    modifiedZ: 'Deviation from peers (MAD z-score)',
    km: 'Mileage in window',
    operatingCost: 'Operating cost',
    peers: 'Comparison vehicles',
    peerGroup: 'Comparison group',
    readingsBelowPrevious: 'Readings below the previous one',
    jumpsAboveLimit: 'Suspiciously large increases',
    maxKmPerDay: 'Largest increase per day',
    fuelEntriesBelowPrevious: 'Fuel entries with lower odometer',
    lastEventPreviousKm: 'Previous reading',
    lastEventKm: 'Reading to check',
    lastEventDate: 'Reading date',
    undocumentedKm: 'Mileage without a logbook',
    segments: 'Segments without a logbook',
    largestSegmentKm: 'Largest segment',
    firstSegmentFrom: 'First segment from',
    lastSegmentTo: 'Last segment to',
    windowDays: 'Analysis window',
    inspectionDueAt: 'Inspection due',
    daysLeft: 'Days to due date',
    plannedInspectionAt: 'Planned inspection',
    avgKmPerDay: 'Average daily mileage',
    expectedKmUntilDue: 'Estimated mileage until due',
    routesCompared: 'Routes compared',
    routesOutsideTolerance: 'Routes outside tolerance',
    routesAbovePlan: 'Above plan',
    routesBelowPlan: 'Below plan',
    plannedKm: 'Planned km',
    actualKm: 'Km from logbooks',
    deviationPct: 'Difference vs plan',
    tolerancePct: 'Tolerance',
    fromForecastOverageKm: 'Source vehicle overage',
    fromForecastEndKm: 'Source vehicle forecast',
    fromLimitKm: 'Source vehicle limit',
    toForecastEndKm: 'Target vehicle forecast',
    toLimitKm: 'Target vehicle limit',
    toHeadroomKm: 'Target vehicle headroom',
    movedKm: 'Proposed shift',
    daysElapsed: 'Lease days elapsed',
    vehiclesWithData: 'Vehicles with data',
    recentIncompleteSegments: 'Incomplete segments (recent)',
    baselineIncompleteSegments: 'Incomplete segments (comparison)',
    routesWithoutLogbook: 'Routes without a logbook',
  },
  insufficient: {
    title: 'Insufficient data',
    description: 'These checks could not be performed. No result does not mean everything is fine — complete the data and the deviations will appear after recomputation.',
    count: '{n} checks without sufficient data',
    fleetWide: 'Whole fleet',
    reason: {
      FUEL_DATA_INCOMPLETE: 'Incomplete fuel segments (no full tank, gap, odometer correction)',
      FUEL_NO_RECENT_DATA: 'No full-to-full segments in the recent period',
      FUEL_NO_BASELINE: 'No consumption history to compare with',
      COST_KM_TOO_LOW: 'Mileage in the window too small or unknown',
      COST_TOO_FEW_PEERS: 'Too few comparable vehicles',
      PLAN_NO_ACTUAL: 'Routes without a linked logbook',
      LEASE_NO_FORECAST: 'No data for the limit forecast',
    },
  },
  recommendations: {
    title: 'Optimisation suggestions',
    description: 'What-if scenarios — to consider, not decisions. Always with assumptions.',
    move: 'Shift about {km} from {from} to {to}',
    savings: 'Potential saving: {amount}',
    assumptions: 'Assumptions',
  },
  summary: {
    title: 'AI summary',
    kindDaily: 'Daily',
    kindWeekly: 'Weekly',
    generatedBy: 'Generated by AI from the facts supplied by deviation detection. The model adds no numbers of its own; verify the data in the cards below.',
    mockNote: 'Demo mode: the summary was assembled from the facts by a template, without a language model.',
    templateNote: 'The model text was rejected (numbers outside the facts or disallowed wording) — showing a template built from the facts.',
    generatedAt: 'Generated {when}',
    stale: 'This summary is stale — refresh it.',
    empty: 'The summary will appear in the next nightly run (06:00) or after clicking “Refresh”.',
    refresh: 'Refresh summary',
    refreshing: 'Generating…',
    refreshed: 'Summary refreshed.',
    regenLimit: 'Today’s limit of AI summary refreshes has been used.',
    regenError: 'Could not refresh the summary. Try again later.',
    noAccess: 'AI summaries are available to accounts with full fleet data scope (lease contracts and logbooks). The deviation list below works without that restriction.',
    loadFailed: 'Could not load the summary.',
    model: 'Model: {model}',
  },
  recompute: { button: 'Recompute now', running: 'Recomputing…', done: 'Deviations recomputed.', failed: 'Could not recompute deviations.' },
  states: {
    loadFailed: 'Could not load deviations.',
    empty: 'No deviations to check. If you see an “Insufficient data” section, some checks could not be performed.',
    emptyFiltered: 'No deviations for the selected filters.',
    excluded: 'Skipped checks that need extra permissions: {kinds}.',
    generatedAt: 'As of {date} · {when}',
    cached: 'from cache',
    fresh: 'freshly computed',
  },
};
